/**
 * Academy Base Controller Constructor.
 * The academy controller keeps track of a users progression through a courses assignments.
 *
 * The academy controller sets up global event listeners on `academy.<course.id>.<course.assignments[i].id>.
 * These event listeners are removed when the course is graduated.
 * If you want to limit the amount of global event listeners, don't construct the academy before `academy.open.<course.id>` event fires.
 *
 * @param View A constructor function for the view. It should take a single `options` argument with `course` and `eventBus` properties
 * @param course          A data structure describing an academy course. Must have the following structure:
 *
 * ``` javascript
 * {
 *   "id": "itemIntroduction", // required, unique per course. Also used as key in the user properties
 *   "assignments": [
 *     // Array of objects with a course unique `id` and a `completed` state
 *     // `completed` will usually be false for most academy courses
 *     {
 *       "id": "login",
 *       "completed": true
 *     },
 *     {
 *       "id": "fillTitle",
 *       "completed": false
 *     }
 *   ]
 * }
 * ```
 */

define('controllers/academy/AcademyBaseController',['require','underscore','jquery','lib/GlobalEventBus','lib/backbone/Backbone.PodioController','models/Podio.Models.User'],function(require) {
  var _ = require('underscore');
  var $ = require('jquery');
  var globalEvent = require('lib/GlobalEventBus');
  var PodioController = require('lib/backbone/Backbone.PodioController');
  var User = require('models/Podio.Models.User');
  var user = new User();

  var userProperty = 'academy_{0}';

  var AcademyBaseController = PodioController.extend({
    initialize: function(course) {
      course = course || {};

      if (!_.isString(course.id) || course.id.length === 0) {
        throw new Error('AcademyBaseController.initialize: Expected course.id to be a string.');
      }

      this.userProperty = userProperty.format(course.id);

      // Get state from the user object, fall back to cloned copy of scaffold
      this.course = user.getProperty(this.userProperty);

      if (_.isNull(this.course)) {
        this.course = $.extend(true, {}, course);
      }

      if (this.course.isDismissed) {
        this.dispose();
        return false;
      }

      this._super();

      if (this.course && !this.isCompleted()) {
        _.bindAll(this, 'closeView', 'openView');
        this._bindEvents();
      }
    },

    _bindEvents: function() {
      var self = this;

      self.listenTo(self.eventBus, 'academy:closeClicked', self.permanentlyDismissAcademy);

      self.course.assignments.forEach(function(assignment) {
        globalEvent.once(['academy', self.course.id, assignment.id].join('.'), self.handleEvent.bind(self, assignment), self);
      });

      globalEvent.on('academy.open.' + self.course.id, self.openView, self);
      globalEvent.on('academy.close.' + self.course.id, self.closeView, self);
    },

    openView: function(callback) {
      if (this._isDisposed) {
        return false;
      }

      if (_.isDefined(this.view) && _.isFunction(this.view.open)) {
        this.view.open(function() {
          this.eventBus.trigger('courseUpdate', this.course);
        }.bind(this));
      }

      return true;
    },

    closeView: function() {
      if (_.isDefined(this.view)) {
        this.view.close();
      }
    },

    permanentlyDismissAcademy: function() {
      this.course.isDismissed = true;
      user.setProperty(this.userProperty, this.course);

      this.dispose();
    },

    isCompleted: function() {
      return this.course && this.course.assignments.every(function(assignment) {
        return assignment.completed;
      });
    },

    isDismissed: function() {
      return !!this._isDisposed;
    },

    handleEvent: function(assignment) {
      if (!assignment.completed) {
        assignment.completed = true;
        assignment.date = (new Date()).toISOString();
        user.setProperty(this.userProperty, this.course);

        this.openView();

        if (this.isCompleted()) {
          // Unbind all events from global event bus
          globalEvent.off(null, null, this);
        }
      }
    },

    dispose: function() {
      this.closeView();

      // Unbind all events from global event bus
      globalEvent.off(null, null, this);
      if (this.eventBus) {
        this.eventBus.off(null, null, this);
      }

      delete this.view;
      delete this.data;
      delete this.course;

      this._isDisposed = true;
    }
  });

  return AcademyBaseController;
});

